// pages/MisClientesPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Grid, Card, CardContent, Typography, Chip, TextField, MenuItem,
  CircularProgress, Alert, Button, Stack, InputAdornment, TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PlaceIcon from "@mui/icons-material/Place";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { alpha } from "@mui/material/styles";
import DashboardLayout from "../layouts/DashboardLayout";
import PageHeader from "../components/layout/PageHeader";
import axios from "../config/axiosConfig";
import { useAuth } from "../context/AuthContext";

const MONEDAS = { 1: "Dólares", 2: "Bolivianos" };
const ESTADOS = ["Activo", "Inactivo", "Todos"];
const EQUIPOS_LABELS = { modem: "Modem", chip: "Chip", radio: "Radio", bateria: "Batería", varios1: "Varios 1", varios2: "Varios 2" };

// Alerta visual para un cliente sin ningún equipo activo (ni placas ni equipos de
// ningún tipo) — útil para detectar servicios facturables pero sin nada instalado.
const esClienteSinEquipos = (c) =>
  (c.placas_activas || 0) === 0 &&
  Object.values(c.equipos || {}).every((cantidad) => Number(cantidad) === 0);

// cliente_servicio.web (plataforma) solo aplica a AVL — alerta si no tiene ninguna
// asignada o si la que tiene está inactiva.
const esAVLSinPlataformaActiva = (c) =>
  c.servicio_nombre === "AVL" && (!c.plataforma || c.plataforma.estado !== "Activo");

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiSelect-select": { py: "6px" },
};

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

export default function MisClientesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [buscarTexto, setBuscarTexto] = useState("");
  const [buscar, setBuscar] = useState("");
  const [estado, setEstado] = useState("Activo");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [ejecutivos, setEjecutivos] = useState([]);
  const [ejecutivo, setEjecutivo] = useState("");
  const [servicios, setServicios] = useState([]);
  const [codServicio, setCodServicio] = useState("Todos");

  useEffect(() => {
    axios.get("/api/dashboard/ejecutivos")
      .then(({ data }) => setEjecutivos(Array.isArray(data) ? data : []))
      .catch(() => setEjecutivos([]));
    axios.get("/api/facturas/servicios")
      .then(({ data }) => setServicios(Array.isArray(data) ? data : []))
      .catch(() => setServicios([]));
  }, []);

  // Por defecto, el usuario logueado — solo se fija una vez que el perfil ya cargó.
  useEffect(() => {
    if (user?.username && !ejecutivo) setEjecutivo(user.username);
  }, [user, ejecutivo]);

  const fetchClientes = useCallback(async (opts = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        buscar,
        estado,
        ejecutivo,
        cod_servicio: codServicio,
        page: opts.page ?? page,
        limit: opts.pageSize ?? pageSize,
      };
      const { data } = await axios.get("/api/dashboard/mis-clientes", { params });
      setClientes(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
      setPage(Number(data?.page || params.page));
      setPageSize(Number(data?.limit || params.limit));
    } catch (e) {
      setError(e?.response?.data?.message || "No se pudo cargar el listado de clientes.");
    } finally {
      setLoading(false);
    }
  }, [buscar, estado, ejecutivo, codServicio, page, pageSize]);

  useEffect(() => {
    if (!ejecutivo) return; // esperar a que se determine el usuario logueado por defecto
    fetchClientes({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscar, estado, ejecutivo, codServicio]);

  const handleBuscar = () => setBuscar(buscarTexto.trim());

  const nombreEjecutivoActual =
    ejecutivos.find((e) => e.username === ejecutivo)?.nombre || ejecutivo;

  return (
    <DashboardLayout>
      <PageHeader
        title="Mis Clientes"
        subtitle={`Servicios (${estado}) a cargo de ${ejecutivo === user?.username ? "ti" : nombreEjecutivoActual} como ejecutivo de ventas${total ? ` · Total: ${total}` : ""}`}
        backTo="/dashboard"
      />
      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Buscar por razón social, código o descripción..."
            value={buscarTexto}
            onChange={(e) => setBuscarTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleBuscar();
            }}
            sx={{ width: { xs: "100%", sm: 340 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            label="Estado"
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
            sx={{ ...compactFieldSx, width: 110 }}
          >
            {ESTADOS.map((e) => <MenuItem key={e} value={e} sx={{ fontSize: "0.85rem" }}>{e}</MenuItem>)}
          </TextField>
          <TextField
            select
            size="small"
            label="Ejecutivo"
            value={ejecutivo}
            onChange={(e) => setEjecutivo(e.target.value)}
            sx={{ ...compactFieldSx, width: 220 }}
          >
            {user?.username && !ejecutivos.some((e) => e.username === user.username) && (
              <MenuItem value={user.username} sx={{ fontSize: "0.85rem" }}>
                {user.username} (yo)
              </MenuItem>
            )}
            {ejecutivos.map((e) => (
              <MenuItem key={e.username} value={e.username} sx={{ fontSize: "0.85rem" }}>
                {e.nombre || e.username}{e.username === user?.username ? " (yo)" : ""}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Servicio"
            value={codServicio}
            onChange={(e) => setCodServicio(e.target.value)}
            sx={{ ...compactFieldSx, width: 160 }}
          >
            <MenuItem value="Todos" sx={{ fontSize: "0.85rem" }}>Todos</MenuItem>
            {servicios.map((s) => (
              <MenuItem key={s.id_servicio} value={String(s.id_servicio)} sx={{ fontSize: "0.85rem" }}>
                {s.nombre}
              </MenuItem>
            ))}
          </TextField>
          <Button size="small" variant="contained" onClick={handleBuscar} sx={{ minHeight: 32 }}>
            Buscar
          </Button>
          {loading && <CircularProgress size={20} />}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        {!loading && clientes.length === 0 ? (
          <Alert severity="info">No se encontraron clientes para los filtros seleccionados.</Alert>
        ) : (
          <Grid container spacing={1.5}>
            {clientes.map((c) => {
              const alertas = [];
              if (esClienteSinEquipos(c)) alertas.push("Sin equipos asignados");
              if (esAVLSinPlataformaActiva(c)) {
                alertas.push(
                  c.plataforma ? `Plataforma inactiva: ${c.plataforma.nombre}` : "Sin plataforma asignada"
                );
              }

              return (
              <Grid key={c.cod_servicio_cliente} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%", display: "flex", flexDirection: "column",
                    ...(alertas.length > 0 && { borderColor: "warning.main" }),
                  }}
                >
                  <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.25 }}>
                        {c.razon_social}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.cod_cliente} · Servicio #{c.cod_servicio_cliente}
                      </Typography>
                    </Box>

                    {alertas.length > 0 && (
                      <Stack spacing={0.4}>
                        {alertas.map((msg) => (
                          <Stack
                            key={msg}
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            sx={{
                              bgcolor: (t) => alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.2 : 0.12),
                              borderRadius: 0.75, px: 0.75, py: 0.4,
                            }}
                          >
                            <WarningAmberIcon sx={{ fontSize: 14, color: "warning.main" }} />
                            <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 700, fontSize: "0.68rem" }}>
                              {msg}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    )}

                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={c.servicio_nombre} color="primary" variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />
                      <Chip size="small" label={c.estado} color={c.estado === "Activo" ? "success" : "default"} sx={{ height: 20, fontSize: "0.68rem" }} />
                      <Chip size="small" label={MONEDAS[c.moneda] || "-"} variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />
                      <Chip size="small" label={`Placas: ${c.placas_activas || 0}`} color="info" variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />
                      {Object.entries(c.equipos || {}).filter(([, cantidad]) => Number(cantidad) > 0).map(([tipo, cantidad]) => (
                        <Chip
                          key={tipo}
                          size="small"
                          label={`${EQUIPOS_LABELS[tipo] || tipo}: ${cantidad}`}
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.68rem" }}
                        />
                      ))}
                    </Stack>

                    {c.descripcion && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.78rem" }}>
                        {c.descripcion}
                      </Typography>
                    )}

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.25, fontSize: "0.72rem" }}>
                      <Typography variant="caption" color="text.secondary">Sucursal: {c.sucursal_facturar || "-"}</Typography>
                      <Typography variant="caption" color="text.secondary">Tipo: {c.tipo || "-"}</Typography>
                      <Typography variant="caption" color="text.secondary">Grupo: {c.grupo || "-"}</Typography>
                      <Typography variant="caption" color="text.secondary">Reg.: {formatFecha(c.servicio_fecha_registro)}</Typography>
                    </Box>

                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                      {(c.telefono || c.celular) && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <PhoneIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {[c.telefono, c.celular].filter(Boolean).join(" / ")}
                          </Typography>
                        </Stack>
                      )}
                      {c.correo_ofi && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <EmailIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary" noWrap>{c.correo_ofi}</Typography>
                        </Stack>
                      )}
                      {c.direccion && (
                        <Stack direction="row" spacing={0.5} alignItems="flex-start">
                          <PlaceIcon sx={{ fontSize: 13, color: "text.secondary", mt: "1px" }} />
                          <Typography variant="caption" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {c.direccion}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>

                    <Box sx={{ flex: 1 }} />

                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<VisibilityIcon fontSize="small" />}
                      onClick={() => navigate(`/reporte-cliente/${c.cod_servicio_cliente}/resumen`, { state: { from: "/mis-clientes" } })}
                      sx={{ alignSelf: "flex-end", fontSize: "0.72rem" }}
                    >
                      Ver detalle
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              );
            })}
          </Grid>
        )}

        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={Math.max(page - 1, 0)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[12, 24, 48, 96]}
            labelRowsPerPage="Por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(_, newPage) => fetchClientes({ page: newPage + 1 })}
            onRowsPerPageChange={(e) => fetchClientes({ page: 1, pageSize: Number(e.target.value) })}
            sx={{ mt: 1 }}
          />
        )}
      </Box>
    </DashboardLayout>
  );
}
